<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import SharedSearchInput from 'src/components/shared/SharedSearchInput.vue';

type SearchResultItem = {
  id: number;
  title: string;
  description: string;
  link: string;
};

type SearchResultGroup = {
  label: string;
  items: SearchResultItem[];
};

const route = useRoute();

const searchQuery = ref('');
const loading = ref(false);

const dummySearchResults: SearchResultGroup[] = [
  {
    label: 'Tickets',
    items: [
      {
        id: 1,
        title: 'Ticket 1',
        description: 'Issue with login',
        link: '/tickets/1',
      },
      {
        id: 2,
        title: 'Ticket 2',
        description: 'Email service down',
        link: '/tickets/2',
      },
      // More dummy ticket items...
    ],
  },
  {
    label: 'Users',
    items: [
      {
        id: 1,
        title: 'User 1',
        description: 'Frontend Developer',
        link: '/users/1',
      },
      {
        id: 2,
        title: 'User 2',
        description: 'Backend Developer',
        link: '/users/2',
      },
      // More dummy user items...
    ],
  },
  // More groups...
];

// Simulate search functionality
const searchResults = computed(() =>
  dummySearchResults.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      item.title.toLowerCase().includes(searchQuery.value.toLowerCase())),
  })));

const onSearch = useDebounceFn(() => {
  loading.value = true;
  // Simulate an API call with a timeout
  setTimeout(() => {
    loading.value = false;
  }, 500);
}, 300);

onMounted(() => {
  const urlQuery = route.query.q;

  if (typeof urlQuery === 'string') {
    searchQuery.value = urlQuery;
  }
});
</script>

<template>
  <SharedBreadCrumb :items="[{ label: 'Global Search' }]" />
  <div class="p-grid mt-3">
    <div class="card p-col-12">
      <SharedSearchInput v-model="searchQuery" />

      <TabView>
        <TabPanel
          v-for="group in searchResults"
          :key="group.label"
          :header="group.label"
        >
          <ul class="result-list">
            <li
              v-for="item in group.items"
              :key="item.id"
            >
              <a :href="item.link">{{ item.title }}</a>
              <p>{{ item.description }}</p>
            </li>
          </ul>
        </TabPanel>
      </TabView>
    </div>
  </div>
</template>
<style scoped>
.result-list {
  list-style: none;
  padding: 0;
}

.result-list li {
  margin-bottom: 0.5em;
}

.p-input-icon-left .pi {
  margin: 0.5em;
}
</style>
